import { useState, useEffect, useCallback } from "react";

export type FavoriteType = "scale" | "chord" | "mode" | "pattern";

export interface FavoriteItem {
  id: string;
  type: FavoriteType;
  title: string;
  subtitle?: string;
  url: string;
  createdAt: number;
}

const STORAGE_KEY = "bordona-student-favorites";

function loadFavorites(): FavoriteItem[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveFavorites(items: FavoriteItem[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
}

export function useStudentFavorites() {
  const [favorites, setFavorites] = useState<FavoriteItem[]>(loadFavorites);

  useEffect(() => {
    saveFavorites(favorites);
  }, [favorites]);

  const addFavorite = useCallback((item: Omit<FavoriteItem, "id" | "createdAt">) => {
    const newItem: FavoriteItem = {
      ...item,
      id: `${item.type}_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      createdAt: Date.now(),
    };
    setFavorites((prev) => [newItem, ...prev]);
    return newItem.id;
  }, []);

  const removeFavorite = useCallback((id: string) => {
    setFavorites((prev) => prev.filter((f) => f.id !== id));
  }, []);

  const isFavorite = useCallback(
    (url: string) => favorites.some((f) => f.url === url),
    [favorites]
  );

  const toggleFavorite = useCallback(
    (item: Omit<FavoriteItem, "id" | "createdAt">) => {
      const existing = favorites.find((f) => f.url === item.url);
      if (existing) {
        removeFavorite(existing.id);
        return false;
      } else {
        addFavorite(item);
        return true;
      }
    },
    [favorites, addFavorite, removeFavorite]
  );

  const clearFavorites = useCallback(() => {
    setFavorites([]);
  }, []);

  return {
    favorites,
    addFavorite,
    removeFavorite,
    isFavorite,
    toggleFavorite,
    clearFavorites,
  };
}
